import React, { useEffect, useState } from 'react';
import { earlyLateReportApi } from '../services/api';
import {
  earlyLateDataRowsToStringRows,
  type EarlyLateReportRow,
} from '../utils/earlyLateReport';
import {
  breakDataRowsToStringRows,
  type BreakReportRow,
  type BreakTakenFilter,
} from '../utils/breakReport';
import { formatCommonFiltersSummary } from '../utils/timecardReportFilters';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/companyDateFormat';
import ReportRunCommonFilterDetails from './ReportRunCommonFilterDetails';
import {
  formatAnalysisFilterSummary,
  formatWeekRangeLabel,
  type TimecardAnalysisFilterParams,
} from '../utils/timecardAnalysisReport';
import {
  formatWeeklyTotalsFilterSummary,
  type WeeklyTotalsFilterParams,
} from '../utils/weeklyTotalsReport';
import {
  Download,
  FileText,
  FileSpreadsheet,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  ChevronUp,
  Trash2,
  Eye,
  FileDown,
} from 'lucide-react';

export interface ReportHistoryItem {
  id: string;
  /** Set when this Early/Late row was persisted via `/api/early-late-reports`. */
  serverEarlyLateId?: number;
  /** Set when this Employee Details row was persisted via `/api/employee-details-report-runs`. */
  serverEmployeeDetailsRunId?: number;
  /** Set when this Payroll Details row was persisted via `/api/payroll-details-report-runs`. */
  serverPayrollDetailsRunId?: number;
  /** Set when this Invoice report row was persisted via `/api/invoice-report-runs`. */
  serverInvoiceReportRunId?: number;
  reportType:
    | 'timecards-analysis'
    | 'timecards-weekly-totals'
    | 'timecards-early-late'
    | 'timecards-break'
    | 'employee-details'
    | 'payroll-details'
    | 'schedules-invoice';
  reportName: string;
  parameters: any;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: Date;
  completedAt?: Date;
  csvUrl?: string;
  excelUrl?: string;
  /** Early/Late report */
  pdfUrl?: string;
  recordsProcessed?: number;
  totalAmount?: number;
  /** Saved at run time for Details — not regenerated */
  earlyLateSnapshot?: {
    headers: string[];
    dataRows: EarlyLateReportRow[];
    /** Company Settings → Currency (e.g. EUR €) */
    currencyLabel?: string;
    /** Minimum net time filter (minutes) used for this run. */
    netMinutesMin?: number;
    parameters?: Record<string, unknown>;
  };
  /** List-only currency when snapshot is not loaded yet (server list). */
  earlyLateCurrencyLabel?: string;
  /** Break report — saved at run time for Details / re-export */
  breakSnapshot?: {
    headers: string[];
    dataRows: BreakReportRow[];
    breakTakenFilter?: BreakTakenFilter;
  };
  /** Employee Details report — snapshot for Details */
  employeeDetailsSnapshot?: {
    headers: string[];
    dataRows: string[][];
  };
  /** Payroll Details report — snapshot for Details */
  payrollDetailsSnapshot?: {
    headers: string[];
    dataRows: string[][];
  };
  /** Invoice report — full editable snapshot */
  invoiceReportSnapshot?: Record<string, unknown>;
  /** Timecards Analysis report — snapshot for Details / preview */
  analysisSnapshot?: {
    headers: string[];
    dataRows: string[][];
  };
  /** Weekly Totals report — snapshot for Details */
  weeklyTotalsSnapshot?: {
    headers: string[];
    dataRows: string[][];
  };
  error?: string;
}

interface ReportHistoryProps {
  reports: ReportHistoryItem[];
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onDeleteReport?: (reportId: string) => void;
}

const ReportHistory: React.FC<ReportHistoryProps> = ({ reports, isExpanded, onToggleExpanded, onDeleteReport }) => {
  const [detailReport, setDetailReport] = useState<ReportHistoryItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (
      !detailReport ||
      detailReport.reportType !== 'timecards-early-late' ||
      detailReport.earlyLateSnapshot ||
      detailReport.serverEarlyLateId == null
    ) {
      setDetailLoading(false);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    earlyLateReportApi
      .get(detailReport.serverEarlyLateId)
      .then((detail) => {
        if (cancelled) return;
        const s = detail.snapshot;
        if (!s?.dataRows?.length) return;
        setDetailReport((prev) =>
          prev && prev.id === detailReport.id
            ? {
                ...prev,
                parameters: { ...prev.parameters, ...(s.parameters ?? {}) },
                earlyLateSnapshot: {
                  headers: s.headers ?? [],
                  dataRows: s.dataRows as EarlyLateReportRow[],
                  currencyLabel: s.currencyLabel ?? detail.currencyLabel,
                  netMinutesMin: s.netMinutesMin ?? detail.netMinutesMin,
                  parameters: s.parameters,
                },
              }
            : prev
        );
      })
      .catch((e) => console.error('Failed to load Early/Late report snapshot', e))
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [detailReport]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'processing':
        return <Loader className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-neutral-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles[status] || 'bg-neutral-100 text-neutral-800 border-neutral-200'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (date: Date) => {
    return formatCompanyDateTime(date);
  };

  const getReportTypeName = (type: string) => {
    switch (type) {
      case 'timecards-analysis':
        return 'Timecards Analysis';
      case 'timecards-weekly-totals':
        return 'Weekly Totals';
      case 'timecards-early-late':
        return 'Early/Late Report';
      case 'timecards-break':
        return 'Break Report';
      case 'employee-details':
        return 'Employee Details';
      case 'payroll-details':
        return 'Payroll Details';
      case 'schedules-invoice':
        return 'Invoice Report';
      default:
        return type;
    }
  };

  const formatParameters = (report: ReportHistoryItem) => {
    if (report.reportType === 'timecards-analysis') {
      if (report.parameters?.fromWeekStartDate && report.parameters?.toWeekStartDate) {
        const summary = formatAnalysisFilterSummary(report.parameters as TimecardAnalysisFilterParams);
        const n = report.recordsProcessed;
        return n != null ? `${summary} · ${n} record${n === 1 ? '' : 's'}` : summary;
      }
      if (report.parameters?.weekStartDate) {
        return `Week: ${formatCompanyDate(report.parameters.weekStartDate)}`;
      }
      return 'Timecards Analysis';
    } else if (report.reportType === 'timecards-weekly-totals') {
      if (report.parameters?.fromWeekStartDate && report.parameters?.toWeekStartDate) {
        const summary = formatWeeklyTotalsFilterSummary(report.parameters as WeeklyTotalsFilterParams);
        const n = report.recordsProcessed;
        return n != null ? `${summary} · ${n} record${n === 1 ? '' : 's'}` : summary;
      }
      return 'Weekly Totals';
    } else if (report.reportType === 'timecards-early-late' && report.parameters?.fromDate && report.parameters?.toDate) {
      const filterSummary = formatCommonFiltersSummary(report.parameters);
      const net = report.parameters.netMinutesMin;
      const netPart = typeof net === 'number' ? ` · Net > ${net} min` : '';
      const base = `${report.parameters.fromDate} → ${report.parameters.toDate}${netPart}`;
      const n = report.recordsProcessed;
      const withCount = n != null ? `${base} · ${n} record${n === 1 ? '' : 's'}` : base;
      return filterSummary ? `${withCount} · ${filterSummary}` : withCount;
    } else if (report.reportType === 'timecards-break' && report.parameters?.fromDate && report.parameters?.toDate) {
      const f = report.parameters.breakTakenFilter;
      const breakPart =
        f === 'yes' ? ' · Break: Yes' : f === 'no' ? ' · Break: No' : '';
      const filterSummary = formatCommonFiltersSummary(report.parameters);
      const base = `${report.parameters.fromDate} → ${report.parameters.toDate}${breakPart}`;
      const n = report.recordsProcessed;
      const withCount = n != null ? `${base} · ${n} record${n === 1 ? '' : 's'}` : base;
      return filterSummary ? `${withCount} · ${filterSummary}` : withCount;
    } else if (report.reportType === 'employee-details' && report.parameters) {
      const n = report.recordsProcessed ?? report.parameters.recordsProcessed ?? 0;
      const st = report.parameters.statusFilter ?? '—';
      const fc = report.parameters.advancedFilterCount ?? 0;
      const cols = report.parameters.outputColumnCount ?? 0;
      return `${n} row${n === 1 ? '' : 's'} · ${st} · ${fc} filter${fc === 1 ? '' : 's'} · ${cols} col${cols === 1 ? '' : 's'}`;
    } else if (report.reportType === 'payroll-details' && report.parameters) {
      const n = report.recordsProcessed ?? report.parameters.recordsProcessed ?? 0;
      const layout = report.parameters.layout === 'summary' ? 'Summary' : 'Detail';
      const run = report.parameters.payrollRunLabel ?? report.parameters.payrollRunId ?? '—';
      const cols = report.parameters.outputColumnCount ?? 0;
      return `${n} row${n === 1 ? '' : 's'} · ${layout} · ${run} · ${cols} col${cols === 1 ? '' : 's'}`;
    }
    return '';
  };

  return (
    <div className="border-t border-neutral-200 bg-white">
      {/* Header */}
      <button
        onClick={onToggleExpanded}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-neutral-600" />
          <div className="text-left">
            <h3 className="text-sm font-semibold text-neutral-900">Report History</h3>
            <p className="text-xs text-neutral-600">
              {reports.length} report{reports.length !== 1 ? 's' : ''} generated
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {reports.filter(r => r.status === 'processing').length > 0 && (
            <span className="flex items-center gap-1 text-xs text-blue-600">
              <Loader className="h-3 w-3 animate-spin" />
              {reports.filter(r => r.status === 'processing').length} processing
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-neutral-400" />
          ) : (
            <ChevronUp className="h-5 w-5 text-neutral-400" />
          )}
        </div>
      </button>

      {/* Report List with Vertical Scrollbar */}
      {isExpanded && (
        <div className="px-6 pb-4">
          {reports.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
              <p className="text-sm">No reports generated yet</p>
              <p className="text-xs mt-1">Generated reports will appear here</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-scroll space-y-1 pr-2" style={{ overflowY: 'scroll' }}>
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="bg-neutral-50 hover:bg-neutral-100 rounded border border-neutral-200 px-4 py-2 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    {/* Report Info - Single Line */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {getStatusIcon(report.status)}
                      
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-neutral-900 truncate">
                          {getReportTypeName(report.reportType)}
                        </span>
                        {getStatusBadge(report.status)}
                      </div>
                      
                      <span className="text-xs text-neutral-600 truncate">
                        {formatParameters(report)}
                      </span>
                      
                      <span className="text-xs text-neutral-400 hidden md:block">
                        {formatDate(report.createdAt)}
                      </span>
                    </div>

                    {/* Actions - Download Links and Delete */}
                    <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                      {report.status === 'completed' && (
                        <>
                          <button
                            type="button"
                            onClick={() => setDetailReport(report)}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-800 bg-neutral-100 border border-neutral-200 rounded hover:bg-neutral-200 transition-colors"
                            title="Report details and downloads"
                          >
                            <Eye className="h-3 w-3" />
                            Details
                          </button>
                          {report.csvUrl && (
                            <a
                              href={report.csvUrl}
                              download={`${report.reportName.replace(/\s+/g, '_')}_${new Date(report.createdAt).toISOString().slice(0, 10)}.csv`}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                              title="Download CSV"
                            >
                              <Download className="h-3 w-3" />
                              CSV
                            </a>
                          )}
                          {report.excelUrl && (
                            <a
                              href={report.excelUrl}
                              download={`${report.reportName.replace(/\s+/g, '_')}_${new Date(report.createdAt).toISOString().slice(0, 10)}.xlsx`}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors"
                              title="Download Excel"
                            >
                              <Download className="h-3 w-3" />
                              Excel
                            </a>
                          )}
                          {report.pdfUrl && (
                            <a
                              href={report.pdfUrl}
                              download={`${report.reportName.replace(/\s+/g, '_')}_${new Date(report.createdAt).toISOString().slice(0, 10)}.pdf`}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded hover:bg-rose-100 transition-colors"
                              title="Download PDF"
                            >
                              <FileDown className="h-3 w-3" />
                              PDF
                            </a>
                          )}
                        </>
                      )}
                      
                      {/* Delete Button */}
                      {onDeleteReport && (
                        <button
                          onClick={() => onDeleteReport(report.id)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                          title="Delete Report"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    
                    {report.error && (
                      <span className="text-xs text-red-600 truncate max-w-xs" title={report.error}>
                        Error: {report.error}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {detailReport && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-history-detail-title"
          onClick={() => setDetailReport(null)}
        >
          <div
            className={`bg-white rounded-lg shadow-xl w-full border border-neutral-200 overflow-hidden flex flex-col ${
              detailReport.reportType === 'timecards-analysis' ||
              detailReport.reportType === 'timecards-weekly-totals' ||
              detailReport.reportType === 'timecards-early-late' ||
              detailReport.reportType === 'timecards-break'
                ? 'max-w-6xl max-h-[92vh]'
                : 'max-w-md p-5'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={
                detailReport.reportType === 'timecards-analysis' ||
                detailReport.reportType === 'timecards-weekly-totals' ||
                detailReport.reportType === 'timecards-early-late' ||
                detailReport.reportType === 'timecards-break'
                  ? 'p-5 shrink-0'
                  : ''
              }
            >
            <h2 id="report-history-detail-title" className="text-lg font-semibold text-neutral-900">
              {detailReport.reportName}
            </h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div>
                <dt className="text-neutral-500 text-xs uppercase tracking-wide">Report run</dt>
                <dd className="text-neutral-900">{formatDate(detailReport.createdAt)}</dd>
              </div>
              {detailReport.reportType === 'timecards-early-late' && detailReport.parameters && (
                <>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">From date</dt>
                    <dd className="text-neutral-900">{detailReport.parameters.fromDate}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">To date</dt>
                    <dd className="text-neutral-900">{detailReport.parameters.toDate}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Net time (mins.) &gt;</dt>
                    <dd className="text-neutral-900">
                      {detailReport.parameters.netMinutesMin ?? detailReport.earlyLateSnapshot?.netMinutesMin ?? '—'}
                    </dd>
                  </div>
                  <ReportRunCommonFilterDetails parameters={detailReport.parameters} />
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Records processed</dt>
                    <dd className="text-neutral-900">{detailReport.recordsProcessed ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Total amount</dt>
                    <dd className="text-neutral-900 font-medium">
                      {detailReport.totalAmount != null
                        ? `${detailReport.totalAmount.toFixed(2)} ${detailReport.earlyLateSnapshot?.currencyLabel ?? detailReport.earlyLateCurrencyLabel ?? ''}`.trim()
                        : '—'}
                    </dd>
                  </div>
                </>
              )}
              {detailReport.reportType === 'timecards-break' && detailReport.parameters && (
                <>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">From date</dt>
                    <dd className="text-neutral-900">{detailReport.parameters.fromDate}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">To date</dt>
                    <dd className="text-neutral-900">{detailReport.parameters.toDate}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Break taken</dt>
                    <dd className="text-neutral-900">
                      {detailReport.parameters.breakTakenFilter === 'yes'
                        ? 'Yes only'
                        : detailReport.parameters.breakTakenFilter === 'no'
                          ? 'No only'
                          : 'All'}
                    </dd>
                  </div>
                  <ReportRunCommonFilterDetails parameters={detailReport.parameters} />
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Records processed</dt>
                    <dd className="text-neutral-900">{detailReport.recordsProcessed ?? '—'}</dd>
                  </div>
                </>
              )}
              {detailReport.reportType === 'timecards-analysis' && detailReport.parameters && (
                <>
                  {detailReport.parameters.fromWeekStartDate && detailReport.parameters.toWeekStartDate ? (
                    <>
                      <div>
                        <dt className="text-neutral-500 text-xs uppercase tracking-wide">Week start</dt>
                        <dd className="text-neutral-900">
                          {formatWeekRangeLabel(detailReport.parameters.fromWeekStartDate)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-neutral-500 text-xs uppercase tracking-wide">Week end</dt>
                        <dd className="text-neutral-900">
                          {formatWeekRangeLabel(detailReport.parameters.toWeekStartDate)}
                        </dd>
                      </div>
                    </>
                  ) : detailReport.parameters.weekStartDate ? (
                    <div>
                      <dt className="text-neutral-500 text-xs uppercase tracking-wide">Week</dt>
                      <dd className="text-neutral-900">{String(detailReport.parameters.weekStartDate)}</dd>
                    </div>
                  ) : null}
                  <ReportRunCommonFilterDetails parameters={detailReport.parameters} />
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Records processed</dt>
                    <dd className="text-neutral-900">{detailReport.recordsProcessed ?? '—'}</dd>
                  </div>
                </>
              )}
              {detailReport.reportType === 'timecards-weekly-totals' && detailReport.parameters && (
                <>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">From week</dt>
                    <dd className="text-neutral-900">
                      {formatWeekRangeLabel(detailReport.parameters.fromWeekStartDate)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">To week</dt>
                    <dd className="text-neutral-900">
                      {formatWeekRangeLabel(detailReport.parameters.toWeekStartDate)}
                    </dd>
                  </div>
                  <ReportRunCommonFilterDetails parameters={detailReport.parameters} />
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Records processed</dt>
                    <dd className="text-neutral-900">{detailReport.recordsProcessed ?? '—'}</dd>
                  </div>
                </>
              )}
              {detailReport.reportType === 'employee-details' && detailReport.parameters && (
                <>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Status filter</dt>
                    <dd className="text-neutral-900">{String(detailReport.parameters.statusFilter ?? '—')}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Rows exported</dt>
                    <dd className="text-neutral-900">{detailReport.recordsProcessed ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Advanced filters</dt>
                    <dd className="text-neutral-900">{detailReport.parameters.advancedFilterCount ?? 0}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Output columns</dt>
                    <dd className="text-neutral-900">{detailReport.parameters.outputColumnCount ?? 0}</dd>
                  </div>
                </>
              )}
              {detailReport.reportType === 'payroll-details' && detailReport.parameters && (
                <>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Layout</dt>
                    <dd className="text-neutral-900">
                      {detailReport.parameters.layout === 'summary' ? 'Summary' : 'Detail'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Payroll run</dt>
                    <dd className="text-neutral-900">
                      {String(detailReport.parameters.payrollRunLabel ?? detailReport.parameters.payrollRunId ?? '—')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Rows exported</dt>
                    <dd className="text-neutral-900">{detailReport.recordsProcessed ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-neutral-500 text-xs uppercase tracking-wide">Output columns</dt>
                    <dd className="text-neutral-900">{detailReport.parameters.outputColumnCount ?? 0}</dd>
                  </div>
                </>
              )}
            </dl>
            <div className="mt-5 flex flex-wrap gap-2">
              {detailReport.csvUrl && (
                <a
                  href={detailReport.csvUrl}
                  download
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                >
                  <Download className="h-3 w-3" /> CSV
                </a>
              )}
              {detailReport.excelUrl && (
                <a
                  href={detailReport.excelUrl}
                  download
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100"
                >
                  <FileSpreadsheet className="h-3 w-3" /> Excel
                </a>
              )}
              {detailReport.pdfUrl && (
                <a
                  href={detailReport.pdfUrl}
                  download
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded hover:bg-rose-100"
                >
                  <FileDown className="h-3 w-3" /> PDF
                </a>
              )}
            </div>
            </div>

            {(detailReport.reportType === 'timecards-analysis' ||
              detailReport.reportType === 'timecards-weekly-totals' ||
              detailReport.reportType === 'timecards-early-late' ||
              detailReport.reportType === 'timecards-break') && (
              <div className="flex-1 overflow-auto min-h-0 px-5 pb-5 border-t border-neutral-100">
                <h3 className="text-sm font-semibold text-neutral-900 pt-4 mb-2">Report records</h3>
                {detailLoading && detailReport.reportType === 'timecards-early-late' ? (
                  <p className="text-sm text-neutral-500">Loading records…</p>
                ) : null}
                {(() => {
                  if (detailLoading && detailReport.reportType === 'timecards-early-late') {
                    return null;
                  }
                  if (detailReport.reportType === 'timecards-analysis') {
                    const snapshot = detailReport.analysisSnapshot;
                    if (snapshot?.dataRows.length) {
                      return (
                        <div className="overflow-x-auto border border-neutral-200 rounded-lg max-h-[50vh] overflow-y-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 sticky top-0">
                              <tr>
                                {snapshot.headers.map((h) => (
                                  <th key={h} className="px-2 py-2 whitespace-nowrap font-medium">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {snapshot.dataRows.map((row, i) => (
                                <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                                  {row.map((cell, j) => (
                                    <td key={j} className="px-2 py-1.5 whitespace-nowrap">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                  }

                  if (detailReport.reportType === 'timecards-weekly-totals') {
                    const snapshot = detailReport.weeklyTotalsSnapshot;
                    if (snapshot?.dataRows.length) {
                      return (
                        <div className="overflow-x-auto border border-neutral-200 rounded-lg max-h-[50vh] overflow-y-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 sticky top-0">
                              <tr>
                                {snapshot.headers.map((h) => (
                                  <th key={h} className="px-2 py-2 whitespace-nowrap font-medium">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {snapshot.dataRows.map((row, i) => (
                                <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                                  {row.map((cell, j) => (
                                    <td key={j} className="px-2 py-1.5 whitespace-nowrap">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                  }

                  if (detailReport.reportType === 'timecards-early-late') {
                    const snapshot = detailReport.earlyLateSnapshot;
                    if (snapshot?.dataRows.length) {
                      const rows = earlyLateDataRowsToStringRows(snapshot.dataRows as EarlyLateReportRow[]);
                      return (
                        <div className="overflow-x-auto border border-neutral-200 rounded-lg max-h-[50vh] overflow-y-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 sticky top-0">
                              <tr>
                                {snapshot.headers.map((h) => (
                                  <th key={h} className="px-2 py-2 whitespace-nowrap font-medium">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, i) => (
                                <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                                  {row.map((cell, j) => (
                                    <td key={j} className="px-2 py-1.5 whitespace-nowrap">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                  }

                  if (detailReport.reportType === 'timecards-break') {
                    const snapshot = detailReport.breakSnapshot;
                    if (snapshot?.dataRows.length) {
                      const rows = breakDataRowsToStringRows(snapshot.dataRows as BreakReportRow[]);
                      return (
                        <div className="overflow-x-auto border border-neutral-200 rounded-lg max-h-[50vh] overflow-y-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 sticky top-0">
                              <tr>
                                {snapshot.headers.map((h) => (
                                  <th key={h} className="px-2 py-2 whitespace-nowrap font-medium">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, i) => (
                                <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                                  {row.map((cell, j) => (
                                    <td key={j} className="px-2 py-1.5 whitespace-nowrap">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                  }

                  return (
                    <p className="text-sm text-neutral-500">
                      No saved row data for this run. Re-generate the report to capture records.
                    </p>
                  );
                })()}
              </div>
            )}

            <div
              className={`flex justify-end ${
                detailReport.reportType === 'timecards-analysis' ||
                detailReport.reportType === 'timecards-weekly-totals' ||
                detailReport.reportType === 'timecards-early-late' ||
                detailReport.reportType === 'timecards-break'
                  ? 'px-5 py-4 border-t border-neutral-100 shrink-0'
                  : 'mt-6'
              }`}
            >
              <button
                type="button"
                onClick={() => setDetailReport(null)}
                className="px-4 py-2 bg-neutral-900 text-white text-sm rounded-md hover:bg-neutral-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportHistory;
